// The account vault on a client (PROTOCOL.md §2): creating it with an account, unlocking it from a passkey's PRF
// output or the recovery key, pairing in both directions, and the project keys it wraps.
import { fromB64u, toB64u } from '../shared/b64.ts';
import * as C from '../shared/crypto.ts';
import { ApiError, type Api, type Tokens } from './api.ts';

type Bytes = Uint8Array<ArrayBuffer>;

export interface NewVault {
  avk: Bytes;
  recoverySecret: Bytes;
  recoveryKey: string;
  /** The `vault` field of /api/signup/verify. */
  upload: { recovery: { wrapped: string; verifier: string }; passkey?: { wrapped: string } };
}

/** A new AVK and recovery key, wrapped for the recovery key and (when PRF gave an output) the passkey. */
export async function newVault(accountId: string, credentialId: string, prfOutput: Uint8Array | null): Promise<NewVault> {
  const avk = C.random(32);
  const recoverySecret = C.random(32);
  const recovery = {
    wrapped: toB64u(await C.seal(await C.recoveryKEK(recoverySecret), avk, C.vaultAAD(accountId, 'recovery'))),
    verifier: await C.recoveryVerifier(recoverySecret),
  };
  const passkey = prfOutput ? { wrapped: await wrapForPasskey(avk, accountId, credentialId, prfOutput) } : undefined;
  return { avk, recoverySecret, recoveryKey: await C.formatRecoveryKey(recoverySecret), upload: { recovery, ...(passkey ? { passkey } : {}) } };
}

export async function wrapForPasskey(avk: Uint8Array, accountId: string, credentialId: string, prfOutput: Uint8Array): Promise<string> {
  return toB64u(await C.seal(await C.passkeyKEK(prfOutput), avk, C.vaultAAD(accountId, credentialId)));
}

/** The AVK from whichever of a passkey's copies opens with its PRF output, else null. */
export async function unlockWithPasskey(accountId: string, credentialId: string, prfOutput: Uint8Array, copies: { wrapped: string }[]): Promise<Bytes | null> {
  const kek = await C.passkeyKEK(prfOutput);
  for (const copy of copies) {
    try {
      return await C.open(kek, fromB64u(copy.wrapped), C.vaultAAD(accountId, credentialId));
    } catch {
      // another copy, for another PRF route
    }
  }
  return null;
}

export async function unlockWithRecovery(accountId: string, recoverySecret: Uint8Array, wrapped: string): Promise<Bytes> {
  return C.open(await C.recoveryKEK(recoverySecret), fromB64u(wrapped), C.vaultAAD(accountId, 'recovery'));
}

/** Replaces the recovery key; returns the new one to show the writer. */
export async function replaceRecoveryKey(api: Api, accountId: string, avk: Uint8Array): Promise<string> {
  const secret = C.random(32);
  await api.call('PUT', '/api/recovery', {
    wrapped: toB64u(await C.seal(await C.recoveryKEK(secret), avk, C.vaultAAD(accountId, 'recovery'))),
    verifier: await C.recoveryVerifier(secret),
  });
  return C.formatRecoveryKey(secret);
}

// Pairing: the new device ------------------------------------------------------------------------------------------

export interface PairingRequest {
  pairingId: string;
  pollToken: string;
  expiresAt: number;
  code: string;
  /** What to show as a QR code (`<origin>/account#<pairingId>.<code>`): the approver opens it, signed in. */
  url: string;
  key: C.EphemeralKey;
}

export async function startPairing(api: Api, device: { name: string; kind: string }): Promise<PairingRequest> {
  const key = await C.ephemeralKey();
  const nonce = C.random(16);
  const r = await api.call<{ pairingId: string; pollToken: string; expiresAt: number }>(
    'POST',
    '/api/pair/start',
    { pub: toB64u(key.pub), nonce: toB64u(nonce), device },
    false,
  );
  const code = await C.pairingCode(key.pub, nonce);
  return { ...r, code, url: `${api.origin}/account#${r.pairingId}.${code.replace('-', '')}`, key };
}

export type PairingResult = { status: 'waiting' } | { status: 'denied' } | { status: 'approved'; avk: Bytes; tokens: Tokens };

/** One poll; an approval signs this device in (the API keeps its tokens) and opens the AVK. */
export async function pollPairing(api: Api, req: PairingRequest): Promise<PairingResult> {
  const r = await api.call<{ status: string; approverPub?: string; sealed?: string } & Partial<Tokens>>(
    'GET',
    `/api/pair/${req.pairingId}/poll?client=${api.client}`,
    undefined,
    false,
    { authorization: `Pair ${req.pollToken}` },
  );
  if (r.status !== 'approved') return { status: r.status as 'waiting' | 'denied' };
  const approverPub = fromB64u(r.approverPub!);
  const k = await C.pairingKey(req.key, approverPub, req.key.pub, approverPub);
  const avk = await C.open(k, fromB64u(r.sealed!), C.pairingAAD(req.pairingId));
  const tokens = api.accept(r as Tokens);
  return { status: 'approved', avk, tokens };
}

export async function waitForPairing(api: Api, req: PairingRequest, everyMs = 1500, signal?: AbortSignal): Promise<PairingResult> {
  while (Date.now() < req.expiresAt && !signal?.aborted) {
    let wait = everyMs;
    try {
      const r = await pollPairing(api, req);
      if (r.status !== 'waiting') return r;
    } catch (e) {
      // Polls are rate-limited per address (30 a minute): a long wait slows down rather than failing.
      if (!(e instanceof ApiError && e.status === 429)) throw e;
      wait = Math.max(everyMs, 10_000);
    }
    await new Promise((resolve) => setTimeout(resolve, wait));
  }
  throw new Error('The pairing request expired before it was approved.');
}

// Pairing: the signed-in device --------------------------------------------------------------------------------------

export interface PairingToApprove {
  pairingId: string;
  device: { name: string; kind: string };
  expiresAt: number;
  /** When the new device asked, and the country its connection came from (two letters), if the relay knows. */
  createdAt?: number;
  place?: string | null;
}

/** Fetches a request and checks its code against the one the writer scanned or typed; throws if they differ. */
export async function checkPairing(api: Api, pairingId: string, code: string): Promise<PairingToApprove & { pub: Bytes }> {
  const r = await api.get<{ pub: string; nonce: string; device: { name: string; kind: string }; expiresAt: number; createdAt?: number; place?: string | null }>(
    `/api/pair/${pairingId}`,
  );
  const pub = fromB64u(r.pub);
  const expected = await C.pairingCode(pub, fromB64u(r.nonce));
  if (!C.sameCode(expected, code)) {
    throw new Error('The code doesn’t match the one on the new device. Don’t approve it: start again on the new device.');
  }
  return { pairingId, device: r.device, expiresAt: r.expiresAt, createdAt: r.createdAt, place: r.place ?? null, pub };
}

/** Finds a waiting request by the code its device shows (when only the code can be typed), then checks it. */
export async function findPairing(api: Api, code: string): Promise<PairingToApprove & { pub: Bytes }> {
  const r = await api.get<{ pairingId: string }>(`/api/pair?code=${encodeURIComponent(code)}`);
  return checkPairing(api, r.pairingId, code);
}

export async function approvePairing(api: Api, req: PairingToApprove & { pub: Uint8Array }, avk: Uint8Array): Promise<string> {
  const own = await C.ephemeralKey();
  const k = await C.pairingKey(own, req.pub, req.pub, own.pub);
  const sealed = await C.seal(k, avk, C.pairingAAD(req.pairingId));
  const r = await api.call<{ deviceId: string }>('POST', `/api/pair/${req.pairingId}/approve`, { approverPub: toB64u(own.pub), sealed: toB64u(sealed) });
  return r.deviceId;
}

// Projects -------------------------------------------------------------------------------------------------------

export interface WebProject {
  id: string;
  wrappedKey: string;
  keyFingerprint: string;
  summary: string | null;
  summaryUpdatedAt: number | null;
  createdAt: number;
  /** Server-readable: Agents on the web is on (PROTOCOL.md §8.3). */
  agents?: boolean;
}

export interface OpenProject {
  id: string;
  key: Bytes;
  summary: C.ProjectSummary | null;
  agents?: boolean;
}

export async function listProjects(api: Api, accountId: string, avk: Uint8Array): Promise<OpenProject[]> {
  const { projects } = await api.get<{ projects: WebProject[] }>('/api/projects');
  return Promise.all(
    projects.map(async (p) => {
      const key = await C.unwrapProjectKey(avk, accountId, p.id, fromB64u(p.wrappedKey));
      const summary = p.summary ? await C.openSummary(key, fromB64u(p.summary)).catch(() => null) : null;
      return { id: p.id, key, summary, agents: !!p.agents };
    }),
  );
}

/** Puts a project on the web with its key wrapped under the AVK. */
export async function putProject(api: Api, accountId: string, avk: Uint8Array, projectId: string, key: Uint8Array, summary?: C.ProjectSummary,
  o: { ifAbsent?: boolean } = {}) {
  await api.call('PUT', `/api/projects/${projectId}`, {
    wrappedKey: toB64u(await C.wrapProjectKey(avk, accountId, projectId, key)),
    keyFingerprint: await C.fingerprint(key),
    ...(summary ? { summary: toB64u(await C.sealSummary(key, summary)) } : {}),
    ...(o.ifAbsent ? { ifAbsent: true } : {}),
  });
}
